import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../../../components/Button';
import { useToast } from '../../../../contexts/useToast';
import {
  AvailabilityData,
  Interval,
  unionIntervals,
  workingIntervals,
} from '../calendar/availability';
import { BookingDetailsDialog, CreateBookingDialog, CreateDraft } from '../calendar/BookingDialogs';
import { CalendarGrid, GridBooking, GridColumn, GridTarget } from '../calendar/CalendarGrid';
import { ResourceFilter } from '../calendar/ResourceFilter';
import {
  addDays,
  formatDate,
  fromZoned,
  startOfWeek,
  todayIn,
  toZoned,
} from '../calendar/calendarTime';
import {
  useAllAvailabilityExceptions,
  useAllAvailabilityRules,
  useBookingMutations,
  useBookings,
  useResources,
  useServices,
} from '../hooks';
import { Booking, Resource, SchedulingApiError, SchedulingProfile } from '../types';

type ViewMode = 'day' | 'week';

const DEFAULT_START = 8 * 60;
const DEFAULT_END = 18 * 60;

export const CalendarTab: React.FC<{ profile: SchedulingProfile }> = ({ profile }) => {
  const { t, i18n } = useTranslation();
  const { showToast } = useToast();
  const timeZone = profile.timezone;

  const [view, setView] = useState<ViewMode>('week');
  const [date, setDate] = useState(() => todayIn(timeZone));
  const [now, setNow] = useState(() => toZoned(new Date(), timeZone));
  // Empty means all resources.
  const [resourceFilter, setResourceFilter] = useState<string[]>([]);

  useEffect(() => {
    const update = () => setNow(toZoned(new Date(), timeZone));
    update();
    const interval = window.setInterval(update, 30_000);
    return () => window.clearInterval(interval);
  }, [timeZone]);
  const [draft, setDraft] = useState<CreateDraft | null>(null);
  const [selected, setSelected] = useState<Booking | null>(null);

  const { data: resources = [] } = useResources(profile.id);
  const { data: services = [] } = useServices(profile.id);
  const { data: rules = [] } = useAllAvailabilityRules();
  const { data: exceptions = [] } = useAllAvailabilityExceptions();
  const { move } = useBookingMutations(profile.id);

  const from = view === 'week' ? startOfWeek(date) : date;
  const dayCount = view === 'week' ? 7 : 1;
  const dates = useMemo(
    () => Array.from({ length: dayCount }, (_, index) => addDays(from, index)),
    [from, dayCount]
  );
  const { data: bookingData = [] } = useBookings(
    profile.id,
    fromZoned(from, 0, timeZone).toISOString(),
    fromZoned(addDays(from, dayCount), 0, timeZone).toISOString()
  );

  const sortedResources = useMemo(
    () => [...resources].sort((a, b) => b.priority - a.priority || a.name.localeCompare(b.name)),
    [resources]
  );
  // The colors follow the position in the full list, so they stay stable under the filter.
  const colorIndexOf = (resourceId: string) =>
    sortedResources.findIndex(resource => resource.id === resourceId);

  const items = useMemo<GridBooking[]>(
    () =>
      bookingData
        .filter(booking => booking.status !== 'cancelled')
        .map(booking => {
          const start = toZoned(booking.start_datetime, timeZone);
          const end = toZoned(booking.end_datetime, timeZone);
          return {
            booking,
            date: start.date,
            start: start.minutes,
            end: end.date === start.date ? end.minutes : 24 * 60,
            colorIndex: colorIndexOf(booking.resource),
          };
        }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [bookingData, timeZone, sortedResources]
  );

  const visibleResources = sortedResources.filter(
    resource =>
      (resourceFilter.length === 0 || resourceFilter.includes(resource.id)) &&
      (resource.is_active || items.some(item => item.booking.resource === resource.id))
  );
  const visibleIds = new Set(visibleResources.map(resource => resource.id));
  const availability: AvailabilityData = { profileId: profile.id, rules, exceptions };
  const today = now.date;

  const warningOf = (item: GridBooking): string | undefined => {
    if (item.booking.status !== 'confirmed') return undefined;
    if (item.date < today || (item.date === today && item.end <= now.minutes)) return undefined;
    const resource = sortedResources.find(candidate => candidate.id === item.booking.resource);
    if (resource && !resource.is_active) return t('schedulingAdmin.calendar.warnings.inactive');
    const covered = workingIntervals(availability, item.booking.resource, item.date).some(
      ([from, to]) => from <= item.start && item.end <= to
    );
    return covered ? undefined : t('schedulingAdmin.calendar.warnings.unavailable');
  };

  const visibleItems = items
    .filter(item => visibleIds.has(item.booking.resource))
    .map(item => ({ ...item, warning: warningOf(item) }));
  const selectedItem = selected ? items.find(item => item.booking.id === selected.id) : undefined;

  const inactiveSuffix = ` (${t('schedulingAdmin.calendar.resourceInactive')})`;
  const displayName = (resource: Resource) =>
    resource.is_active ? resource.name : `${resource.name}${inactiveSuffix}`;
  // An inactive resource has no working hours: the engine offers no slots for it.
  const intervalsOf = (resource: Resource, day: string): Interval[] =>
    resource.is_active ? workingIntervals(availability, resource.id, day) : [];

  const lanes = visibleResources.map(resource => ({
    resourceId: resource.id,
    name: displayName(resource),
    colorIndex: colorIndexOf(resource.id),
    inactive: !resource.is_active,
  }));

  const columns: GridColumn[] =
    view === 'day'
      ? visibleResources.map(resource => ({
          key: resource.id,
          date,
          resourceId: resource.id,
          title: displayName(resource),
          colorIndex: colorIndexOf(resource.id),
          inactive: !resource.is_active,
          narrow: false,
          isToday: date === today,
          intervals: intervalsOf(resource, date),
          lanes: [],
          laneIntervals: [],
        }))
      : dates.map(day => {
          const laneIntervals = visibleResources.map(resource => intervalsOf(resource, day));
          const intervals = unionIntervals(laneIntervals);
          const hasBookings = visibleItems.some(item => item.date === day);
          return {
            key: day,
            date: day,
            resourceId: null,
            title: formatDate(day, i18n.language, { weekday: 'short', day: 'numeric' }),
            narrow: intervals.length === 0 && !hasBookings,
            isToday: day === today,
            intervals,
            lanes,
            laneIntervals,
          };
        });

  const allIntervals: Interval[] = columns.flatMap(column => column.intervals);
  const minutes = [...allIntervals.flat(), ...visibleItems.flatMap(item => [item.start, item.end])];
  const startMinute =
    minutes.length > 0 ? Math.floor(Math.min(...minutes) / 60) * 60 : DEFAULT_START;
  const endMinute = minutes.length > 0 ? Math.ceil(Math.max(...minutes) / 60) * 60 : DEFAULT_END;

  const shift = (direction: number) => setDate(addDays(date, direction * dayCount));

  const rangeLabel =
    view === 'day'
      ? formatDate(date, i18n.language, {
          weekday: 'long',
          day: 'numeric',
          month: 'long',
          year: 'numeric',
        })
      : `${formatDate(from, i18n.language, { day: 'numeric', month: 'short' })} – ${formatDate(
          addDays(from, 6),
          i18n.language,
          { day: 'numeric', month: 'short', year: 'numeric' }
        )}`;

  const moveErrorMessage = (error: unknown): string => {
    if (error instanceof SchedulingApiError) {
      const byCode: Record<string, string> = {
        slot_unavailable: 'occupied',
        resource_inactive: 'inactiveTarget',
        service_not_offered: 'serviceNotOffered',
        booking_not_confirmed: 'notConfirmed',
        scheduling_not_available: 'notAllowed',
        profile_inactive: 'profileInactive',
        start_in_past: 'startInPast',
        appointment_started: 'started',
      };
      const key = (error.code && byCode[error.code]) || (error.status === 409 && 'occupied');
      if (key) return t(`schedulingAdmin.calendar.errors.${key}`);
    }
    return t('schedulingAdmin.calendar.errors.moveFailed');
  };

  const handleMove = async (booking: Booking, target: GridTarget) => {
    const refuse = (key: string) =>
      showToast({ type: 'error', message: t(`schedulingAdmin.calendar.errors.${key}`) });
    // The API refuses any move that ends on an inactive resource, even the booking's own.
    const targetResource = sortedResources.find(
      resource => resource.id === (target.resourceId ?? booking.resource)
    );
    if (targetResource && !targetResource.is_active) {
      refuse('inactiveTarget');
      return;
    }
    // Same rules as the API: a started appointment stays, and none moves into the past.
    if (new Date(booking.start_datetime) <= new Date()) {
      refuse('started');
      return;
    }
    if (fromZoned(target.date, target.minutes, timeZone) <= new Date()) {
      refuse('startInPast');
      return;
    }
    const moving = items.find(item => item.booking.id === booking.id);
    if (targetResource && moving) {
      const length = moving.end - moving.start;
      const open = workingIntervals(availability, targetResource.id, target.date).some(
        ([from, to]) => from <= target.minutes && target.minutes + length <= to
      );
      if (!open) {
        refuse('closedTarget');
        return;
      }
    }
    try {
      await move.mutateAsync({
        id: booking.id,
        data: {
          start_datetime: fromZoned(target.date, target.minutes, timeZone).toISOString(),
          ...(target.resourceId && target.resourceId !== booking.resource
            ? { resource: target.resourceId }
            : {}),
        },
      });
      showToast({ type: 'success', message: t('schedulingAdmin.calendar.moved') });
    } catch (error) {
      showToast({ type: 'error', message: moveErrorMessage(error) });
    }
  };

  // An inactive calendar is read-only: bookings can be viewed, nothing else.
  const readOnly = !profile.is_active;
  const refuseReadOnly = () =>
    showToast({ type: 'error', message: t('schedulingAdmin.calendar.errors.profileInactive') });

  const filterResources = sortedResources
    .filter(resource => resource.is_active)
    .map(resource => ({
      id: resource.id,
      name: resource.name,
      colorIndex: colorIndexOf(resource.id),
    }));

  const toggle = (mode: ViewMode) => (
    <button
      key={mode}
      type="button"
      onClick={() => setView(mode)}
      className={`px-3 py-1.5 text-sm font-medium ${
        view === mode
          ? 'bg-blue-600 text-white'
          : 'bg-white text-gray-700 hover:bg-gray-50 dark:bg-gray-900 dark:text-gray-300 dark:hover:bg-gray-800'
      }`}
    >
      {t(`schedulingAdmin.calendar.${mode}`)}
    </button>
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" onClick={() => shift(-1)} title={t('common.back')}>
            <span className="material-icons">chevron_left</span>
          </Button>
          <Button variant="secondary" size="sm" onClick={() => setDate(todayIn(timeZone))}>
            {t('schedulingAdmin.calendar.today')}
          </Button>
          <Button variant="ghost" size="sm" onClick={() => shift(1)}>
            <span className="material-icons">chevron_right</span>
          </Button>
        </div>
        <h2 className="text-base font-semibold text-gray-900 dark:text-white">{rangeLabel}</h2>
        <div className="ml-auto flex flex-wrap items-center gap-3">
          <div className="flex overflow-hidden rounded-lg border border-gray-300 dark:border-gray-600">
            {(['day', 'week'] as const).map(toggle)}
          </div>
          {!readOnly && (
            <Button
              onClick={() =>
                setDraft({
                  date,
                  minutes: Math.max(startMinute, DEFAULT_START),
                  resourceId: resourceFilter.length === 1 ? resourceFilter[0] : null,
                })
              }
            >
              {t('schedulingAdmin.calendar.newBooking')}
            </Button>
          )}
        </div>
      </div>

      {readOnly && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-900 dark:bg-amber-900/20 dark:text-amber-200">
          <span className="material-icons text-lg leading-none">info</span>
          <span>{t('schedulingAdmin.calendar.errors.profileInactive')}</span>
        </div>
      )}

      <ResourceFilter
        resources={filterResources}
        selected={resourceFilter}
        onChange={setResourceFilter}
      />

      {visibleResources.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {t('schedulingAdmin.calendar.noResources')}
        </p>
      ) : (
        <>
          <CalendarGrid
            columns={columns}
            bookings={visibleItems}
            startMinute={startMinute}
            endMinute={endMinute}
            granularity={profile.slot_granularity_minutes}
            now={now}
            onCreate={readOnly ? refuseReadOnly : setDraft}
            onOpen={setSelected}
            onMove={readOnly ? refuseReadOnly : handleMove}
          />
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {t('schedulingAdmin.calendar.legend')}
            {profile.send_confirmation_sms &&
              ` ${t('schedulingAdmin.calendar.customerInformed')}`}
          </p>
        </>
      )}

      <CreateBookingDialog
        profile={profile}
        draft={draft}
        services={services}
        resources={resources}
        onClose={() => setDraft(null)}
      />
      <BookingDetailsDialog
        profile={profile}
        booking={selected}
        warning={selectedItem ? warningOf(selectedItem) : undefined}
        onClose={() => setSelected(null)}
      />
    </div>
  );
};
